/**
 * RLQ — ServiceTypePicker
 *
 * Renders the service-type grid for the kiosk check-in.
 *
 * Reads from (priority order):
 *   1. `config.systemSettings['lobby.serviceTypes']` (JSON-encoded array) if present.
 *   2. `FALLBACK_SERVICE_TYPES` (hardcoded in types.ts).
 *
 * ConfigBundle does NOT have a `serviceTypes` top-level field today; the
 * RLQ config route is PLANNED. Until then we treat system-settings as the
 * forward-compatible key.
 */
import { useMemo } from 'react';
import { CardRadioGroup } from '@/components/primitives';
import type { CardRadioOption } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';
import { FALLBACK_SERVICE_TYPES } from '../types';
import type { ServiceTypeOption } from '../types';

export interface ServiceTypePickerProps {
  selected: string | null;
  onChange: (code: string) => void;
}

/** Parse `config.systemSettings['lobby.serviceTypes']` if present. */
function readConfigServiceTypes(
  systemSettings: Record<string, unknown>,
): ReadonlyArray<ServiceTypeOption> | null {
  const raw = systemSettings['lobby.serviceTypes'];
  if (raw === undefined || raw === null) return null;

  // Accept either a stringified JSON array or a pre-parsed array.
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(parsed)) return null;

  const out: ServiceTypeOption[] = [];
  for (const entry of parsed) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    if (typeof e.code !== 'string' || typeof e.label !== 'string') continue;
    out.push({
      code: e.code,
      label: e.label,
      helper: typeof e.helper === 'string' ? e.helper : undefined,
      requiredPermission: (typeof e.requiredPermission === 'string'
        ? e.requiredPermission
        : 'Any') as ServiceTypeOption['requiredPermission'],
      targetModule: (typeof e.targetModule === 'string'
        ? e.targetModule
        : 'RCC') as ServiceTypeOption['targetModule'],
    });
  }
  return out.length > 0 ? out : null;
}

export function useServiceTypes(): ReadonlyArray<ServiceTypeOption> {
  const { config } = useConfig();
  return useMemo<ReadonlyArray<ServiceTypeOption>>(() => {
    const fromConfig = readConfigServiceTypes(config.systemSettings);
    return fromConfig ?? FALLBACK_SERVICE_TYPES;
  }, [config.systemSettings]);
}

export function ServiceTypePicker({
  selected,
  onChange,
}: ServiceTypePickerProps): JSX.Element {
  const options = useServiceTypes();

  const radioOptions: CardRadioOption[] = options.map((o) => ({
    value: o.code,
    primary: o.label,
    helper: o.helper ?? '',
  }));

  return (
    <CardRadioGroup
      name="lobby-service-type"
      options={radioOptions}
      selected={selected}
      onChange={onChange}
    />
  );
}
